import { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

export const DEFAULT_TYPES = [
  ["REGHRS", "Regular"], ["OTHRS", "Overtime"], ["DBLTIME", "Double Time"], ["VAC", "Vacation"], ["SICKPD", "Sick"],
  ["PERSONAL", "Personal"], ["BERPD", "Bereavement"], ["STATHOL", "Statutory Holiday"], ["STATWRK", "Statutory Holiday Worked"],
  ["TRAIN", "Training"], ["TRAVEL", "Travel"], ["ONCALL", "On Call"], ["CALLIN", "Call In"], ["UNPAIDLV", "Unpaid Leave"],
];

const hrsOf = (e) => (e.code && !["REGHRS", "OTHRS", "DBLTIME", "TRAIN", "TRAVEL", "ONCALL", "CALLIN", "STATWRK"].includes(e.code))
  ? Number(e.hours_paid ?? e.hours ?? 0) : Number(e.hours_worked ?? e.hours ?? 0);

export default function TimeDayDetailModal({ companyId, employee, date, entries, dayNote, codes, editable, onClose, onSaved }) {
  const [rows, setRows] = useState({});
  const [note, setNote] = useState(dayNote || "");
  const [extra, setExtra] = useState("");
  useEffect(() => {
    const m = {};
    (entries || []).forEach((e) => { const c = e.code || "REGHRS"; m[c] = { hours: hrsOf(e), note: e.note || "" }; });
    setRows(m);
  }, [entries]);
  const visible = [...DEFAULT_TYPES.map(([c]) => c), ...Object.keys(rows).filter((c) => !DEFAULT_TYPES.some(([d]) => d === c))];
  const label = (c) => DEFAULT_TYPES.find(([d]) => d === c)?.[1] || codes.find((x) => x.code === c)?.name || c;
  const total = visible.reduce((t, c) => t + (parseFloat(rows[c]?.hours) || 0), 0);
  const set = (c, k, v) => setRows((s) => ({ ...s, [c]: { ...(s[c] || { hours: "", note: "" }), [k]: v } }));
  const save = async () => {
    const payload = visible.filter((c) => rows[c] && (parseFloat(rows[c].hours) || rows[c].note)).map((c) => ({ code: c, hours: parseFloat(rows[c].hours) || 0, note: rows[c].note || "" }));
    try {
      await api.put(`/companies/${companyId}/timesheets/day`, { employee_id: employee.employee_id, date, entries: payload, day_note: note });
      toast.success("Day saved"); onSaved?.(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const dateLabel = new Date(date + "T12:00:00").toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" });
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="day-detail-modal" onClick={onClose}>
      <div className="bg-white border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{employee.first_name} {employee.last_name}</div>
            <h3 className="text-lg font-black tracking-tight" data-testid="day-detail-title">{dateLabel}</h3>
          </div>
          <button data-testid="day-detail-close" onClick={onClose} className="h-8 w-8 flex items-center justify-center border border-border hover:border-black"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-3 space-y-1">
          {visible.map((c) => (
            <div key={c} className="grid grid-cols-[1fr_80px_1fr] items-center gap-2 py-0.5">
              <span className="text-sm">{label(c)}<span className="ml-1 text-[9px] font-mono text-muted-foreground">{c}</span></span>
              <input data-testid={`day-hours-${c}`} type="number" step="0.25" min="0" disabled={!editable} value={rows[c]?.hours ?? ""} placeholder="0.00"
                onChange={(e) => set(c, "hours", e.target.value)} className="border border-input px-2 py-1 text-sm text-right tabular-nums disabled:bg-neutral-100" />
              <input data-testid={`day-note-${c}`} disabled={!editable} value={rows[c]?.note ?? ""} placeholder="Entry note" onChange={(e) => set(c, "note", e.target.value)} className="border border-input px-2 py-1 text-xs disabled:bg-neutral-100" />
            </div>
          ))}
          {editable && (
            <div className="flex items-center gap-2 pt-2">
              <select data-testid="day-add-code" value={extra} onChange={(e) => setExtra(e.target.value)} className="border border-input px-2 py-1 text-xs flex-1">
                <option value="">Add another time type…</option>
                {codes.filter((x) => !visible.includes(x.code)).map((x) => <option key={x.code} value={x.code}>{x.code} — {x.name}</option>)}
              </select>
              <button data-testid="day-add-code-btn" onClick={() => { if (extra) { set(extra, "hours", ""); setExtra(""); } }} className="border border-border px-2 py-1 text-xs hover:border-black">Add</button>
            </div>
          )}
          <div className="pt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Day note</div>
            <textarea data-testid="day-note" disabled={!editable} value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full border border-input px-2 py-1 text-sm disabled:bg-neutral-100" placeholder="e.g. Approved overtime, left early for appointment" />
          </div>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-neutral-50">
          <div className="text-sm">Total <span data-testid="day-total" className="font-black tabular-nums text-lg ml-1">{total.toFixed(2)}</span></div>
          <div className="flex gap-2">
            <button onClick={onClose} className="border border-border px-3 py-1.5 text-sm hover:border-black">Cancel</button>
            {editable && <button data-testid="day-save" onClick={save} className="border border-black bg-black text-white px-4 py-1.5 text-sm font-medium hover:bg-white hover:text-black transition-colors">Save day</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
